import React, { useEffect, useState } from "react";
import { Copy, Check, Edit3, Save, RotateCcw, Megaphone } from "lucide-react";
import { toast } from "sonner";

/**
 * Marketing snippets — ready-to-paste copy for Anthony's manual outreach
 * (Facebook DMs to new followers, launch posts, comment-to-DM triggers).
 *
 * Stored in localStorage so edits persist locally without needing a
 * backend collection. Tap "Copy" → text is on the clipboard, tap once
 * more on the platform of choice and paste.
 */

const DEFAULT_SNIPPETS = [
  {
    id: "fb-welcome-follower",
    title: "Facebook DM · Thanks for the follow",
    description: "Paste into a Messenger DM when you spot a new follower on the page. Fits comfortably without 'see more' cut.",
    body: `Hey 👋 Thanks so much for the follow!

Welcome to CampCompass UK — we're a small UK team that just launched the country's biggest campsite comparison tool. 22,000+ UK campsites, all side-by-side, so you can find the cheapest, dog-friendliest, most-loved sites in seconds. No more flipping between 12 different tabs to plan a weekend away.

👉 Free sign-up here (takes 20 seconds): https://camp-compass.uk

Once you're in you can:
✓ Save your favourite sites
✓ Get 48-hour weather alerts before your trip
✓ Share your own trips & photos on the Wall
✓ Book in one tap via Pitchup, the site's website, or by phone

Would love to hear what you think — and where you're camping next! 🏕

— Anthony @ CampCompass UK`,
  },
  {
    id: "fb-welcome-short",
    title: "Facebook DM · Short version",
    description: "Punchier alternative for fast-scrollers — under 400 chars.",
    body: `Hey 👋 Thanks for the follow!

We've put 22,000+ UK campsites into one free app — compare prices, distances, dog policies, the lot, in seconds.

👉 https://camp-compass.uk (free, 20-second sign-up)

Would love to know where you're camping next! 🏕

— Anthony @ CampCompass UK`,
  },
  {
    id: "fb-launch-post",
    title: "🚀 LAUNCH DAY · Facebook / Instagram post",
    description: "Pin this to the top of your Facebook + Instagram for launch day (31 May).",
    body: `🚀 LIVE NOW!!

CampCompass UK is officially OPEN — the biggest UK campsite comparison ever built.

🏕 22,000+ UK campsites · one app · one tap
💰 Save up to £42/night by comparing
🐕 Filter by dog-friendly, hot showers, electric hook-up
🌦 48-hour weather alerts before every trip
📸 Trip Wall — see real camper photos
🤝 Campfire community — 11 active threads on day one

👉 Sign up FREE: https://camp-compass.uk
🎁 First 14 days of Pro on us — cancel anytime, no auto-charge if you don't love it.

Tag every camper you know. Let's fill the map together 🏴󠁧󠁢󠁥󠁮󠁧󠁿⛺

#ukcamping #campsiteuk #ukcampsites #campingweekend #campinguk #launchday`,
  },
  {
    id: "wa-launch-blast",
    title: "🚀 LAUNCH DAY · WhatsApp blast (personal)",
    description: "Drop into 5-10 personal WhatsApp chats / groups on launch morning.",
    body: `Hey 👋

CampCompass UK is LIVE today! 🚀

It's the biggest UK campsite map ever built (22,000+ sites) — free to browse, plus 14 days of Pro free for everyone signing up today.

Would mean the world if you'd give it a try and forward to one camping mate 🏕

👉 https://camp-compass.uk

— Anthony`,
  },
  {
    id: "ig-launch-caption",
    title: "🚀 LAUNCH DAY · Instagram caption",
    description: "Use under your launch reel / carousel post.",
    body: `🚀 LIVE NOW.

After months of building, CampCompass UK is officially open today.

The biggest UK campsite database in one app — 22,000+ sites, all side-by-side. Free while we grow. Every early sign-up keeps their free access when paid tiers launch 🎁

Save your shortlist · weather alerts · trip wall · dog filter · honest reviews · book in one tap.

Link in bio 👆 or head to camp-compass.uk

Tag the camping mate you're going with next 👇

#ukcamping #campsiteuk #ukcamping2026 #campervanlife #tentlife #familycamping #ukcampsites #launchday`,
  },
];

const STORAGE_KEY = "cc_admin_marketing_snippets_v2_launchday";

export default function MarketingSnippetsCard() {
  const [snippets, setSnippets] = useState(DEFAULT_SNIPPETS);
  const [copiedId, setCopiedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  // Hydrate from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) setSnippets(parsed);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const persist = (next) => {
    setSnippets(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const onCopy = async (sn) => {
    try {
      await navigator.clipboard.writeText(sn.body);
      setCopiedId(sn.id);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedId((id) => (id === sn.id ? null : id)), 2000);
    } catch {
      toast.error("Couldn't copy — try selecting the text manually.");
    }
  };

  const onEdit = (sn) => {
    setEditingId(sn.id);
    setDraft(sn.body);
  };

  const onSave = (sn) => {
    const next = snippets.map((x) => (x.id === sn.id ? { ...x, body: draft } : x));
    persist(next);
    setEditingId(null);
    setDraft("");
    toast.success("Saved");
  };

  const onReset = (sn) => {
    const original = DEFAULT_SNIPPETS.find((d) => d.id === sn.id);
    if (!original) return;
    const next = snippets.map((x) => (x.id === sn.id ? { ...x, body: original.body } : x));
    persist(next);
    setEditingId(null);
    toast.success("Reset to default");
  };

  return (
    <div className="border hairline-strong p-5 bg-white" data-testid="admin-marketing-snippets-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-[#fff4ee] border hairline flex items-center justify-center flex-shrink-0">
          <Megaphone className="w-5 h-5 text-[#ff4500]" strokeWidth={1.75} />
        </div>
        <div>
          <h2 className="font-display text-xl leading-tight">Marketing snippets</h2>
          <p className="text-[12px] mono text-[#666] mt-0.5">
            Ready-to-paste copy for Facebook DMs, launch posts, etc. One tap to copy. Edits save automatically to your browser.
          </p>
        </div>
      </div>

      <ul className="space-y-4" data-testid="marketing-snippets-list">
        {snippets.map((sn) => {
          const isEditing = editingId === sn.id;
          const isCopied = copiedId === sn.id;
          return (
            <li key={sn.id} className="border hairline" data-testid={`marketing-snippet-${sn.id}`}>
              <div className="flex items-start justify-between gap-3 px-4 py-3 border-b hairline bg-[#fafafa]">
                <div className="min-w-0">
                  <div className="font-display text-base leading-tight">{sn.title}</div>
                  <div className="text-[11px] mono text-[#666] mt-0.5">{sn.description}</div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {!isEditing && (
                    <>
                      <button
                        onClick={() => onCopy(sn)}
                        className={`inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider px-2.5 py-1.5 border hairline transition-colors ${isCopied ? "bg-[#1f7a35] text-white border-[#1f7a35]" : "hover:bg-[#fff4ee] hover:border-[#ff4500] hover:text-[#ff4500]"}`}
                        data-testid={`marketing-snippet-copy-${sn.id}`}
                      >
                        {isCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {isCopied ? "Copied" : "Copy"}
                      </button>
                      <button
                        onClick={() => onEdit(sn)}
                        className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider px-2.5 py-1.5 border hairline hover:bg-[#fff4ee]"
                        data-testid={`marketing-snippet-edit-${sn.id}`}
                        title="Edit"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                  {isEditing && (
                    <>
                      <button
                        onClick={() => onSave(sn)}
                        className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider px-2.5 py-1.5 bg-[#ff4500] text-white border hairline border-[#ff4500] hover:bg-[#e63d00]"
                        data-testid={`marketing-snippet-save-${sn.id}`}
                      >
                        <Save className="w-3 h-3" /> Save
                      </button>
                      <button
                        onClick={() => onReset(sn)}
                        className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider px-2.5 py-1.5 border hairline hover:bg-[#fff4ee]"
                        data-testid={`marketing-snippet-reset-${sn.id}`}
                        title="Reset to default"
                      >
                        <RotateCcw className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setDraft(""); }}
                        className="text-[11px] mono underline text-[#666] px-1"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="p-4">
                {isEditing ? (
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="w-full border hairline px-3 py-2 text-sm leading-relaxed resize-y min-h-[280px] font-sans"
                    data-testid={`marketing-snippet-textarea-${sn.id}`}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm leading-relaxed text-[#222] font-sans" data-testid={`marketing-snippet-body-${sn.id}`}>
                    {sn.body}
                  </pre>
                )}
                <div className="mt-2 text-[10px] mono text-[#888] text-right">
                  {(isEditing ? draft : sn.body).length} chars
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
